/**
 * FlowStoreRef — module-level access to the mounted ReactFlow store.
 *
 * ReactFlow's onlyRenderVisibleElements unmounts node components when they
 * scroll off-screen, so any generation lifecycle work that lives inside a
 * node component can die (or go stale) mid-run. The store itself, however,
 * outlives node components: it is owned by the <ReactFlow> instance at the
 * canvas level. This module captures the store-bound functions once when
 * the canvas mounts so module-level services (GenerationLifecycleService)
 * can read and mutate the graph with ZERO mounted node components.
 *
 * Populated by CreativeNodeFlow on mount, cleared on unmount. Callers must
 * handle the null case (canvas not mounted — e.g. user navigated to the
 * dashboard while a generation was still running).
 */

import type { Node, Edge } from '@xyflow/react';

export interface FlowStoreFunctions {
  setNodes: (payload: Node[] | ((nodes: Node[]) => Node[])) => void;
  setEdges: (payload: Edge[] | ((edges: Edge[]) => Edge[])) => void;
  getNode: (id: string) => Node | undefined;
  getNodes: () => Node[];
  getEdges: () => Edge[];
}

let current: FlowStoreFunctions | null = null;

export function registerFlowStore(fns: FlowStoreFunctions): void {
  current = fns;
}

export function unregisterFlowStore(): void {
  current = null;
}

/** Returns the mounted canvas store functions, or null when no canvas is mounted. */
export function getFlowStore(): FlowStoreFunctions | null {
  return current;
}
